import { Label, Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@creanizer/ui'
import { Globe, Users } from 'lucide-react'
import type { SocietyInput, SavedWorldBuilding, SavedCharacter, SavedCommunity } from '@creanizer/types'
import { generateWorldContextText, generateCharacterContext, generateCommunityContext } from '@creanizer/utils'

interface ConnectionsFormProps {
  input: SocietyInput
  onChange: (input: SocietyInput) => void
  savedWorlds: SavedWorldBuilding[]
  savedCharacters: SavedCharacter[]
  savedCommunities: SavedCommunity[]
}

export function ConnectionsForm({
  input,
  onChange,
  savedWorlds,
  savedCharacters,
  savedCommunities
}: ConnectionsFormProps) {
  const handleWorldSelect = (worldId: string) => {
    if (worldId === 'none') {
      onChange({ ...input, worldContext: undefined })
      return
    }
    const world = savedWorlds.find(w => w.id === worldId)
    if (world) {
      const worldContext = generateWorldContextText(world.result)
      onChange({ ...input, worldContext })
    }
  }

  const handleCharacterSelect = (characterIds: string[]) => {
    const selectedChars = savedCharacters.filter(c => characterIds.includes(c.id))
    const contexts = selectedChars.map(char => generateCharacterContext(char))
    
    onChange({
      ...input,
      relatedCharacterIds: characterIds,
      relatedCharacterContext: contexts.length > 0 ? contexts.join('\n\n') : undefined
    })
  }

  const handleCommunitySelect = (communityId: string) => {
    if (communityId === 'none') {
      onChange({ ...input, relatedCommunityContext: undefined })
      return
    }
    const community = savedCommunities.find(c => c.id === communityId)
    if (community) {
      const communityContext = generateCommunityContext(community)
      onChange({ ...input, relatedCommunityContext: communityContext })
    }
  }

  return (
    <div className="space-y-4">
      {savedWorlds.length > 0 && (
        <div className="space-y-2">
          <Label htmlFor="worldSelect" className="flex items-center gap-2">
            <Globe className="h-4 w-4" />
            世界観連携
          </Label>
          <Select onValueChange={handleWorldSelect}>
            <SelectTrigger id="worldSelect">
              <SelectValue placeholder="世界観を選択（省略可）" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">なし</SelectItem>
              {savedWorlds.map((world) => (
                <SelectItem key={world.id} value={world.id}>
                  {world.title}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {savedCharacters.length > 0 && (
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <Users className="h-4 w-4" />
            <Label>関連キャラクター（複数選択可）</Label>
          </div>
          <div className="grid grid-cols-2 gap-2 max-h-40 overflow-y-auto border rounded-md p-2">
            {savedCharacters.map((character) => (
              <label
                key={character.id}
                className="flex items-center space-x-2 p-2 border rounded cursor-pointer hover:bg-accent transition-colors"
              >
                <input
                  type="checkbox"
                  checked={input.relatedCharacterIds?.includes(character.id) || false}
                  onChange={(e) => {
                    const ids = input.relatedCharacterIds || []
                    const newIds = e.target.checked
                      ? [...ids, character.id]
                      : ids.filter(id => id !== character.id)
                    handleCharacterSelect(newIds)
                  }}
                  className="rounded"
                />
                <span className="text-sm truncate" title={character.title}>
                  {character.title}
                </span>
              </label>
            ))}
          </div>
          {input.relatedCharacterIds && input.relatedCharacterIds.length > 0 && (
            <p className="text-xs text-muted-foreground">
              選択中: {input.relatedCharacterIds.length}名のキャラクター
            </p>
          )}
        </div>
      )}

      {savedCommunities.length > 0 && (
        <div className="space-y-2">
          <Label htmlFor="communitySelect">関連コミュニティ（本拠地など）</Label>
          <Select onValueChange={handleCommunitySelect}>
            <SelectTrigger id="communitySelect">
              <SelectValue placeholder="コミュニティを選択（省略可）" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">なし</SelectItem>
              {savedCommunities.map((community) => (
                <SelectItem key={community.id} value={community.id}>
                  {community.title}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {savedWorlds.length === 0 && savedCharacters.length === 0 && savedCommunities.length === 0 && (
        <div className="p-4 bg-muted rounded-lg">
          <p className="text-sm text-muted-foreground">
            他のジェネレータで作成した世界観、キャラクター、コミュニティと連携できます。
            まず他のジェネレータでデータを作成してください。
          </p>
        </div>
      )}
    </div>
  )
}